import { useState, useRef } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Progress } from '@creanizer/ui'
import { AlertCircle } from 'lucide-react'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useStreamGenerator } from '../../../../hooks/useStreamGenerator'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { useWikipediaStore } from '../../../../stores/wikipediaStore'
import type { WikipediaInput, WikipediaResult } from '@creanizer/types'
import { WikipediaForm } from './WikipediaForm'
import { WikipediaDisplay } from './WikipediaDisplay'
import { SavedWikipediaList } from './SavedWikipediaList'

export function WikipediaGenerator() {
  const { isApiKeySet } = useSettingsStore()
  const { savedWikipedias, addWikipedia } = useWikipediaStore()
  const generator = useGenerator('wikipedia')
  const streamGenerator = useStreamGenerator('wikipedia')
  
  const [input, setInput] = useState<WikipediaInput>({
    detailLevel: 5,
    articleLength: 'medium'
  })
  const [result, setResult] = useState<WikipediaResult | null>(null)
  const [error, setError] = useState<string>('')
  const [useStreaming, setUseStreaming] = useState<boolean>(true)
  const [activeTab, setActiveTab] = useState('generate')
  const resultRef = useRef<HTMLDivElement>(null)

  const handleGenerate = async () => {
    if (!isApiKeySet()) {
      setError('APIキーが設定されていません。設定ページから設定してください。')
      return
    }

    setError('')
    setResult(null)

    try {
      if (useStreaming) {
        await streamGenerator.generateStream(input, {
          onChunk: (chunk) => {
            console.log('Received chunk:', chunk.length)
          },
          onProgress: (bytes) => {
            console.log('Progress:', bytes)
          },
          onComplete: (resultData) => {
            // Ensure result has required properties
            if (resultData && resultData.title) {
              setResult(resultData as WikipediaResult)
              setTimeout(() => {
                resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }, 100)
            } else {
              console.error('Incomplete result received:', resultData)
              setError('結果のパースに失敗しました')
            }
          },
          onError: (error) => {
            console.error('Streaming error:', error)
            setError(error.message)
            
            if (error.message.includes('ストリーミング')) {
              setUseStreaming(false)
              generator.mutate(input)
            }
          }
        })
      } else {
        const response = await generator.mutateAsync(input)
        const resultData = response.result || response
        setResult(resultData as WikipediaResult)
        
        setTimeout(() => {
          resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
      }
    } catch (err) {
      console.error('Generation error:', err)
      setError(err instanceof Error ? err.message : '生成中にエラーが発生しました')
    }
  }

  const isSaved = () => {
    if (!result || !result.title) return false
    return savedWikipedias.some(saved => 
      saved?.result?.title === result.title
    )
  }

  const handleSave = () => {
    if (!result) return
    
    addWikipedia({
      title: result.title,
      input,
      result
    })
    // 状態を更新するために新しいオブジェクトで再レンダリング
    setResult({...result})
  }

  const handleLoad = (wikipedia: any) => {
    setInput(wikipedia.input)
    setResult(wikipedia.result)
    setActiveTab('generate')
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <h2 className="text-xl font-semibold mb-6">架空事典ジェネレータ</h2>
        <div>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="generate">生成</TabsTrigger>
              <TabsTrigger value="saved">
                保存済み ({savedWikipedias.length}/{500})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="generate" className="space-y-4">
              <WikipediaForm
                input={input}
                onChange={setInput}
                onGenerate={handleGenerate}
                isGenerating={generator.isPending || streamGenerator.isStreaming}
                useStreaming={useStreaming}
                onStreamingChange={setUseStreaming}
              />
              
              {streamGenerator.isStreaming && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>生成中...</span>
                    <span>{Math.round(streamGenerator.progress)}%</span>
                  </div>
                  <Progress value={streamGenerator.progress} />
                  {streamGenerator.partialContent && (
                    <div className="mt-4 p-4 bg-muted rounded max-h-48 overflow-y-auto">
                      <pre className="text-xs whitespace-pre-wrap">
                        {streamGenerator.partialContent.slice(-500)}
                        {streamGenerator.partialContent.length > 500 && '...'}
                      </pre>
                    </div>
                  )}
                </div>
              )}
              
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </TabsContent>
            
            <TabsContent value="saved">
              <SavedWikipediaList
                wikipedias={savedWikipedias}
                onLoad={handleLoad}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {result && result.title && (
        <div ref={resultRef}>
          <WikipediaDisplay
            result={result}
            onSave={handleSave}
            isSaved={isSaved()}
          />
        </div>
      )}
    </div>
  )
}